import {LanguageEnum} from '@/enums';
import {differenceInYears, isAfter, isEqual, isValid} from 'date-fns';
import {WritableComputedRef} from 'vue';
import {ComposerTranslation} from 'vue-i18n';

export class DateHelper {

    public static fromUTCStringToDate(utcString?: string): Date | null {
        return utcString ? new Date(utcString) : null;
    }

    public static fromThreeUTCStringToDate(day: number, month: number, year: number): Date | null {
        return (year && month && day) ? new Date(year, (month - 1), day) : null;
    }

    public static fromUtcStringToUtcStringWithoutHours(utcString?: string): string | null {
        const dateConvert = this.fromUTCStringToDate(utcString);
        return dateConvert ? this.fromDateToUTCString(dateConvert) : null;
    }

    public static fromStringDDMMYYYYToDate(date: string, splitSeparator= "/" ): Date {
        const dateArray = date.split(splitSeparator);

        const year = dateArray[2];
        const month = dateArray[1];
        const day = dateArray[0];
        return new Date(`${month}/${day}/${year}`);
    }

    /**
     * Provide an UTC string representation of a Date.
     * /!\ If the date is not defined or not valid, return undefined
     * @param date The date created with ISO format
     * @param withTime true for a DateTime ISO Format, false for a Date ISO format
     */
    public static fromDateToUTCString(date: Date, withTime = false): string {
        if (!DateHelper.isDateDefinedAndValid(date)) {
            return '';
        }
        const year = date.getFullYear().toString().padStart(4, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return withTime ?
            `${day}/${month}/${year} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}` :
            `${day}/${month}/${year}`;
    }

    public static fromDateToYmd(date: Date): string {
        if (!DateHelper.isDateDefinedAndValid(date)) {
            return '';
        }

        const year = date.getFullYear().toString().padStart(4, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    public static isDateDefinedAndValid(date: Date): boolean {
        return isValid(date);
    }

    public static getWithoutHours(date: Date): Date | null {
        if (!date) {
            return null;
        }
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    /**
     * @param refDate the reference date we want to be after
     * @param toCompare the date we want the referenced to be compared to
     * @param orSame
     */
    public static isAfterOrSame(refDate: Date, toCompare: Date, orSame = true): boolean {
        if (!refDate) {
            return true;
        }
        const refDateCopy = this.getWithoutHours(refDate);
        const toCompareCopy = this.getWithoutHours(toCompare);
        if (!refDateCopy || !toCompareCopy) {
            return false;
        }
        return orSame ?
            isAfter(refDateCopy, toCompareCopy) || isEqual(refDateCopy, toCompareCopy) :
            isAfter(refDateCopy, toCompareCopy);
    }

    public static getAge(birthDate: Date): number {
        return differenceInYears(new Date(), birthDate);
    }

    public static compareWithoutHours(date1: Date, date2: Date): number {
        const date1WithoutHours = this.getWithoutHours(date1);
        const date2WithoutHours = this.getWithoutHours(date2);
        const date1Time = date1WithoutHours?.getTime();
        const date2Time = date2WithoutHours?.getTime();
        if (!date1Time || !date2Time) {
            return -1;
        }
        if (date1Time === date2Time) {
            return 0;
        } else if (date1Time > date2Time) {
            return 1;
        } else {
            return -1;
        }
    }

    public static changeDayToLastOfMonth(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    }

    public static onlyMonth(date: Date): string {
        return (date.getMonth() + 1).toString().padStart(2, '0');
    }

    public static translateDateTime(locale: string|WritableComputedRef<string>, t: ComposerTranslation, date: Date, forceQuoteBeforeYear = false): string {
        if (isNaN(date.getTime())) {
            console.error("Invalid date, using current date instead.");
            date = new Date();
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().substring(2);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        const monthTranslation = t(`DATE.MONTH.${month}`);
        const atTranslation = t('DATE.AT');
        const localeValue = typeof locale === 'string' ? locale : locale.value;

        return `${day} 
                ${monthTranslation} 
                ${forceQuoteBeforeYear || localeValue === LanguageEnum.nl ? `'` : ''}${year} 
                ${atTranslation} 
                ${hours}:${minutes}`;
    }

    /**
     * get Month Between Two Dates
     * @param date1
     * @param date2 (Default current date)
     * Output a number or null
     */
    public static getMonthBetweenTwoDates(date1: Date, date2: Date = new Date()): number | null {
        if (!date1) {
            return null;
        }

        const differenceYears = date2.getFullYear() - date1.getFullYear();
        return differenceYears * 12 + (date2.getMonth() - date1.getMonth());
    }
}
